/****************
 * The notification bell in the header.
 *
 * Shows what happened to every order the traders you copy placed — whether it
 * executed, or was filtered and why. A dot appears when there is something new;
 * opening the panel clears it.
 *
 * The outcome for each row comes from lib/orderOutcome.ts, which is a port of
 * the mobile taxonomy. It is deliberately not re-derived here: the ordering in
 * that file encodes a fix for a real reported bug — an exchange rejection
 * displaying as "Filtered by your strategy", blaming the user's own config for
 * a venue outage.
 ****************/

import { actionLabel } from "@/lib/actionLabel";
import { useAssetName } from '@/lib/instruments';
import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';
import { Bell } from '@phosphor-icons/react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { useNotifications, useUnreadCount } from '@/hooks/useNotifications';
import { useNotices } from '@/hooks/useNotices';
import { useSessions } from '@/hooks/useSessions';
import {
  OUTCOME_TONE,
  labelFor,
  outcomeOf,
  summariseOutcomes,
  type ReplicatedOrderLike,
} from '@/lib/orderOutcome';
import { mergeFeed, NOTIFICATION_PAGE, NOTIFICATION_PAGE_STEP, timeAgoOf } from '@/lib/notifications.core';
import { focusLink } from '@/lib/focusLink.core';

/** The shared rule (lib/notifications.core.ts), so the phone says the same. */
function timeAgo(t: TFunction, iso?: string): string {
  const ago = timeAgoOf(iso, Date.now());
  return ago ? t(`common:${ago.key}`, ago.n === undefined ? undefined : { n: ago.n }) : '';
}

export function NotificationRow({
  order,
  isNew,
  broker,
  traderName,
  onSelect,
}: {
  order: ReplicatedOrderLike;
  isNew: boolean;
  broker?: string;
  traderName?: string;
  onSelect?: (order: ReplicatedOrderLike) => void;
}) {
  const { t } = useTranslation('common');
  const an = useAssetName();
  const outcome = outcomeOf(order);
  const rawAction = (order.master_action_type || '').toUpperCase();

  /*
   * A session starting or ending is not an order, and reads badly as one.
   *
   * It has no symbol, so the title fell through to "Unknown", and its action is
   * the raw enum SESSION_STOPPED. Given the trader's name it says
   * "RapidPanther296 / session ended" instead, which is the sentence Guillaume
   * asked for.
   *
   * ── Why this is a map and not a ternary ─────────────────────────────────
   *
   * It used to be `SESSION_STARTED ? 'session started' : 'session ended'` —
   * two events assumed to be the only two. They are not, and the fallback was
   * not neutral: it ASSERTED that the session had ended. So the moment a third
   * lifecycle event existed, it announced a false ending.
   *
   * Two already exist. SESSION_SETTINGS_CHANGED (S21, shipped 2026-09-10) told
   * a user their session had ended when all they had done was change a leverage
   * cap on a session that was still running. SESSION_POSITIONS_CLOSED (S23)
   * would have done the same on a session that ended hours earlier.
   *
   * A map, so an unknown event falls through to a sentence that claims nothing
   * rather than to the most alarming one available. Adding an event to
   * recordLifecycle without adding it here is now merely vague, not wrong.
   */
  /*
   * actionLabel unconditionally: it returns a non-lifecycle action unchanged by
   * contract, so gating it on `outcome === 'lifecycle'` was a SECOND, separate
   * signal for one fact — execution_status here, master_action_type inside
   * actionLabel. They agree only because recordLifecycle writes both in the
   * same struct literal. Any SESSION_* row reaching the bell without
   * execution_status='lifecycle' fell through and printed the bare enum, which
   * is the defect this file was changed to fix. (obsidian-b3, audit of S26.)
   */
  const isLifecycle = outcome === 'lifecycle';
  const action = actionLabel(rawAction);
  const clickable = Boolean(onSelect && order.subscription_id);

  return (
    <div
      className={`px-4 py-3 border-b border-border/50 last:border-0 ${
        clickable ? 'cursor-pointer hover:bg-accent/50 focus-visible:bg-accent/50 focus-visible:outline-none' : ''
      }`}
      role={clickable ? 'button' : undefined}
      tabIndex={clickable ? 0 : undefined}
      onClick={clickable ? () => onSelect!(order) : undefined}
      onKeyDown={
        clickable
          ? (e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect!(order);
              }
            }
          : undefined
      }
    >
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            {isNew && (
              <span
                className="h-1.5 w-1.5 rounded-full bg-primary shrink-0"
                aria-label={t('bell.new')}
              />
            )}
            <span className="font-medium text-sm truncate">
              {(order.symbol && an(order.symbol)) || (isLifecycle ? traderName || t('bell.yourSession') : t('bell.unknown'))}
            </span>
            {action && (
              <span className="text-[11px] uppercase tracking-wide text-muted-foreground">
                {action}
              </span>
            )}
          </div>

          {/****************
            * WHICH SESSION THIS CAME FROM.
            *
            * Guillaume, 2026-09-14: clicking a notification gave "not much info
            * about where it comes from". The trader name alone does not answer
            * it — a user can run several sessions on the SAME trader with
            * different leverage, size or stops, and "Copying RapidPanther296"
            * is true of all of them. The session number is the part that is
            * actionable: it is what the URL, the session list and a support
            * thread all use.
            *
            * Shown even when the trader is unknown, which is the case the old
            * line dropped entirely: a notification whose trader we could not
            * resolve rendered with no provenance at all, and the session number
            * is exactly what is left to identify it by.
            ****************/}
          {(traderName || order.subscription_id) && (
            <div className="text-[11px] text-muted-foreground mt-0.5 truncate">
              {order.subscription_id ? (
                <span className="tabular-nums">{t('bell.session', { id: order.subscription_id })}</span>
              ) : null}
              {order.subscription_id && traderName ? ' · ' : ''}
              {traderName ? t('bell.copying', { name: traderName }) : ''}
            </div>
          )}

          <div className={`text-xs mt-0.5 font-medium ${OUTCOME_TONE[outcome]}`}>
            {labelFor(outcome, broker)}
          </div>

          {/* Deliberately no explanation, no backend detail, no next step.
              A notification had become a copy of the order row it points at,
              which is a wall of text to scroll past and makes the click
              pointless — you have already read everything. Trader, symbol,
              action, verdict, time. The reasoning is one tap away, on the page
              that has room for it. Guillaume, 2026-09-03. */}
        </div>

        <span className="text-[11px] text-muted-foreground shrink-0 pt-0.5">
          {timeAgo(t, order.created_at)}
        </span>
      </div>
    </div>
  );
}

const PAGE = NOTIFICATION_PAGE;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  /* How many rows are rendered. Reset on close so re-opening is cheap again. */
  const [visible, setVisible] = useState(PAGE);
  const navigate = useNavigate();
  const { t } = useTranslation('common');

  const { data, isLoading, error } = useNotifications();
  const orders = useMemo(() => data ?? [], [data]);

  const { unread: orderUnread, markAllSeen: markOrdersSeen, lastSeenId } = useUnreadCount(data);
  // A backtest's results are ready (11-03), beside the orders.
  const { notices, unread: noticeUnread, seen: noticeSeen, markSeen: markNoticesSeen } = useNotices();
  const unread = orderUnread + noticeUnread;
  const markAllSeen = () => { markOrdersSeen(); markNoticesSeen(); };
  const feed = useMemo(() => mergeFeed(notices, orders), [notices, orders]);
  const newestNoticeId = notices.reduce((m, n) => Math.max(m, n.id), 0);
  const summary = useMemo(() => summariseOutcomes(orders), [orders]);

  // Resolve the trader behind each order. Already cached by React Query for
  // the sessions page, so this is not an extra round trip in practice.
  const { data: sessions } = useSessions();
  const traderBySub = useMemo(() => {
    const m = new Map<number, string>();
    (sessions ?? []).forEach((s: any) => m.set(Number(s.id), s.trader_name));
    return m;
  }, [sessions]);

  // Land on the order itself, not the top of the session. SessionDetails reads
  // ?order=, opens the Orders tab, scrolls to the row and marks it.
  const openOrder = (order: ReplicatedOrderLike) => {
    if (!order.subscription_id) return;
    setOpen(false);
    navigate(focusLink({ kind: 'order', sessionId: order.subscription_id, orderId: order.id }, 'web'));
  };

  const handleOpenChange = (next: boolean) => {
    setOpen(next);
    // Back to five rows every time it opens, so a session that once scrolled
    // to fifty does not pay for fifty on every subsequent open.
    setVisible(PAGE);
    // Mark on open, not on close: the point at which they have been seen.
    if (next) markAllSeen();
  };

  return (
    <Popover open={open} onOpenChange={handleOpenChange}>
      <PopoverTrigger asChild>
        <Button
          variant="ghost"
          size="sm"
          className="relative h-8 w-8 p-0"
          data-tour="nav-notifications"
          aria-label={unread > 0 ? t('bell.labelNew', { n: unread }) : t('bell.label')}
          title={t('bell.label')}
        >
          <Bell size={20} />
          {unread > 0 && (
            <span className="absolute top-1 end-1 flex items-center justify-center min-w-[16px] h-4 px-1 rounded-full bg-primary text-primary-foreground text-[10px] font-bold leading-none">
              {unread > 99 ? '99+' : unread}
            </span>
          )}
        </Button>
      </PopoverTrigger>

      <PopoverContent align="end" className="w-[380px] p-0">
        <div className="px-4 py-3 border-b border-border">
          <div className="flex items-center justify-between">
            <h3 className="font-semibold text-sm">{t('bell.title')}</h3>
            <span className="text-xs text-muted-foreground">
              {t('bell.summary', { executed: summary.executed, filtered: summary.filtered, failed: summary.failed })}
            </span>
          </div>
          <p className="text-[11px] text-muted-foreground mt-0.5">
            {t('bell.liveNote')}
          </p>
        </div>

        {/* Not ScrollArea: its viewport is h-full, and a percentage height
            against a max-height-only parent resolves to auto. The content then
            overflowed a root with overflow-hidden and everything past the first
            screenful was unreachable -- the reported "panel will not scroll".
            A plain max-height + overflow-y-auto scrolls AND still shrinks to
            fit a short list. 2026-09-02. */}
        {/* Five rows to start, ten more each time the list is scrolled near its
            end. Rendering fifty order rows — each computing its outcome, label,
            explanation and next step — is most of the cost of opening this
            panel, and almost none of it is ever read. Guillaume, 2026-09-03:
            "just load the first 5 and make it so the other one load when we
            scroll down". */}
        <div
          className="max-h-[420px] overflow-y-auto overscroll-contain"
          onScroll={(e) => {
            const el = e.currentTarget;
            if (el.scrollHeight - el.scrollTop - el.clientHeight < 120) {
              setVisible((n) => (n < feed.length ? n + NOTIFICATION_PAGE_STEP : n));
            }
          }}
        >
          {/* An outage empties `orders`. Without this the panel would say
              "nothing yet" to someone whose session is running — the same
              failure the audit found on the sessions page. */}
          {error ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-muted-foreground">
                {t('bell.error')}
              </p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                {t('bell.errorHint')}
              </p>
            </div>
          ) : isLoading ? (
            <div className="px-4 py-8 text-center text-sm text-muted-foreground">
              {t('bell.loading')}
            </div>
          ) : orders.length === 0 && notices.length === 0 ? (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-muted-foreground">{t('bell.empty')}</p>
              <p className="text-xs text-muted-foreground/70 mt-1">
                {t('bell.emptyHint')}
              </p>
            </div>
          ) : (
            /* One list, newest first (Guillaume 11-03: "Backtest result should be mixed with the
               rest of the notifications and everything should be filtered by Recency"): a
               backtest's notice sits among the orders at its own time, never in a block of its own. */
            feed.slice(0, visible).map((item) => item.kind === 'notice' ? (
              <button key={`n${item.n.id}`} type="button"
                onClick={() => { setOpen(false); navigate(item.n.web); }}
                className={`relative w-full text-start px-4 py-3 border-b border-border hover:bg-muted/40 transition-colors ${item.n.id > noticeSeen ? 'bg-primary/5' : ''}`}>
                {/* The guide's anchor is a literal on the newest notice only, so the tour-target scan can read it. */}
                {item.n.id === newestNoticeId ? <span data-tour="notif-backtest-ready" aria-hidden className="pointer-events-none absolute inset-0" /> : null}
                <p className="text-sm font-medium">{t(item.n.status === 'failed' ? 'bell.backtestFailed' : 'bell.backtestReady')}</p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {item.n.title ? `${item.n.title} · ` : ''}{timeAgo(t, item.n.created_at)}
                </p>
              </button>
            ) : (
              /* No broker name passed: the web has no credentials endpoint,
                 and adding one for a label is not worth an extra round trip.
                 The default reads "your broker", which is true on any venue —
                 unlike naming Phemex, which becomes a lie the day MEXC lands. */
              <NotificationRow
                key={item.o.id}
                order={item.o}
                isNew={item.o.id > lastSeenId}
                traderName={item.o.subscription_id ? traderBySub.get(Number(item.o.subscription_id)) : undefined}
                onSelect={openOrder}
              />
            ))
          )}

          {/* Loading more cannot depend on the list overflowing. Making the rows
              light — which was the point — meant five of them no longer filled
              the 420px box, so there was nothing to scroll, the scroll handler
              never fired, and everything older was unreachable. An explicit
              control works whether or not the list is tall enough to scroll; the
              scroll handler is the shortcut, not the mechanism.
              Guillaume, 2026-09-03. */}
          {visible < feed.length && (
            <button
              className="w-full px-4 py-2.5 text-xs text-primary hover:bg-muted/40 border-t border-border transition-colors"
              onClick={() => setVisible((n) => n + NOTIFICATION_PAGE_STEP)}
            >
              {t('bell.showMore', { n: Math.min(NOTIFICATION_PAGE_STEP, feed.length - visible) })}
              <span className="text-muted-foreground">{t('bell.older', { n: feed.length - visible })}</span>
            </button>
          )}
        </div>

        {orders.length > 0 && (
          <div className="px-4 py-2.5 border-t border-border">
            <button
              className="text-xs text-primary hover:underline"
              onClick={() => {
                setOpen(false);
                navigate('/activity');
              }}
            >
              {t('bell.viewAll')}
            </button>
          </div>
        )}
      </PopoverContent>
    </Popover>
  );
}
